<template>
  <page>
    <page-header :title="$t('SshKeyManagement_Title')">
      <template slot="actions">
        <el-button
          v-if="$can('manageProjectTechnical')"
          type="primary"
          icon="el-icon-circle-plus"
          @click="openCreateDialog('generate')"
        >
          {{ $t('SshKeyManagement_Button_CreateKey') }}
        </el-button>
        <el-button
          v-if="$can('manageProjectTechnical')"
          icon="el-icon-upload2"
          @click="openCreateDialog('import')"
        >
          {{ $t('SshKeyManagement_Button_ImportKey') }}
        </el-button>
      </template>
    </page-header>

    <page-content>
      <rest-list :payload="fetchEndpoint" ref="list">
        <rest-list-column
          prop="name"
          :title="$t('SshKeyManagement_Column_Name')"
          :sortable="true"
          :filterable="true"
        />
        <rest-list-column
          prop="public_key_fingerprint"
          :title="$t('SshKeyManagement_Column_Fingerprint')"
          :sortable="false"
          :filterable="false"
        />
        <rest-list-column
          prop="key_type"
          :title="$t('SshKeyManagement_Column_Type')"
          :sortable="false"
          :filterable="false"
          :width="120"
        >
          <template slot-scope="scope">
            {{ getKeyTypeLabel(scope.row.key_type) }}
          </template>
        </rest-list-column>
        <rest-list-column
          prop="used_by_integrations"
          :title="$t('SshKeyManagement_Column_UsedBy')"
          :sortable="false"
          :filterable="false"
        >
          <template slot-scope="scope">
            <span v-if="scope.row.used_by_integrations.length === 0">
              {{ $t('SshKeyManagement_NotUsed') }}
            </span>
            <el-popover v-else trigger="hover" placement="top">
              <div class="integration-list">
                <router-link
                  v-for="integration in scope.row.used_by_integrations"
                  :key="integration.id"
                  :to="getIntegrationRoute(integration.id)"
                  class="integration-list-item"
                >
                  {{ integration.name }}
                </router-link>
              </div>
              <el-tag slot="reference" type="info" size="small">
                {{ scope.row.used_by_integrations.length }}
                {{ $t('SshKeyManagement_Integrations') }}
              </el-tag>
            </el-popover>
          </template>
        </rest-list-column>
        <rest-list-column-date
          prop="create_time"
          :title="$t('SshKeyManagement_Column_Created')"
          :sortable="true"
          :filterable="false"
        />
        <rest-list-column-actions>
          <template slot-scope="scope">
            <rest-list-action
              icon="el-icon-view"
              :title="$t('SshKeyManagement_Button_ViewKey')"
              :click="() => showPublicKey(scope.row)"
            />
            <rest-list-action-delete
              v-if="
                $can('manageProjectTechnical') &&
                  scope.row.used_by_integrations.length === 0
              "
              :endpoint="deleteEndpoint(scope.row)"
              :name="scope.row.name"
            />
          </template>
        </rest-list-column-actions>
      </rest-list>
    </page-content>

    <ssh-key-generate-dialog
      :visible.sync="showGenerateDialog"
      @created="onKeyCreated"
    />

    <ssh-key-import-dialog
      :visible.sync="showImportDialog"
      @created="onKeyCreated"
    />

    <ssh-key-view-dialog :visible.sync="showViewDialog" :ssh-key="viewKey" />
  </page>
</template>

<script>
import Page from '@/common/components/page/Page'
import PageHeader from '@/common/components/page/PageHeader'
import PageContent from '@/common/components/page/PageContent'
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListAction from '@/common/components/rest/RestListAction'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import SshKeyGenerateDialog from '@/common/components/ssh/SshKeyGenerateDialog.vue'
import SshKeyImportDialog from '@/common/components/ssh/SshKeyImportDialog.vue'
import SshKeyViewDialog from '@/common/components/ssh/SshKeyViewDialog.vue'
import sshKeyTypesMixin from '@/common/mixins/sshKeyTypesMixin'

export default {
  name: 'SshKeyManagementPage',

  mixins: [sshKeyTypesMixin],

  components: {
    Page,
    PageHeader,
    PageContent,
    RestList,
    RestListColumn,
    RestListColumnDate,
    RestListColumnActions,
    RestListAction,
    RestListActionDelete,
    SshKeyGenerateDialog,
    SshKeyImportDialog,
    SshKeyViewDialog
  },

  data() {
    return {
      showGenerateDialog: false,
      showImportDialog: false,
      showViewDialog: false,
      viewKey: null
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'ssh-keys',
        params: {
          sort: '-create_time',
          'per-page': 20
        }
      })
    }
  },

  mounted() {
    this.fetchKeyTypes()
  },

  methods: {
    openCreateDialog(mode) {
      if (mode === 'generate') {
        this.showGenerateDialog = true
      } else {
        this.showImportDialog = true
      }
    },

    onKeyCreated(data) {
      this.$message({
        message: this.$t('SshKeyManagement_Message_Created'),
        type: 'success'
      })
      this.$refs.list.refresh()
      this.viewKey = data
      this.showViewDialog = true
    },

    showPublicKey(row) {
      this.viewKey = row
      this.showViewDialog = true
    },

    deleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `ssh-keys/${row.id}`
      })
    },

    getIntegrationRoute(integrationId) {
      return projectRoute({
        name: 'IntegrationSettings',
        query: { id: integrationId }
      })
    }
  }
}
</script>

<style scoped>
.key-details {
  padding: 0 20px;
}

.key-section {
  margin-bottom: 20px;
}

.key-section label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #606266;
}

.key-value {
  font-family: monospace;
  background: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  word-break: break-all;
}

.key-copy-container {
  position: relative;
}

.copy-button {
  position: absolute;
  top: 5px;
  right: 10px;
  font-size: 18px;
  z-index: 1;
}

>>> .key-copy-container textarea {
  font-family: monospace;
  font-size: 12px;
}

.field-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

>>> .el-form-item textarea {
  font-family: monospace;
  font-size: 12px;
}

.integration-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.integration-list-item {
  display: block;
  color: #409eff;
  text-decoration: none;
}

.integration-list-item:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/pages/SshKeyManagementPage.vue